/**
 * useJsmTickets
 *
 * Composable for the JSM two-way support chat widget.
 *
 * Responsibilities:
 *  - Persist submitted ticket records in localStorage so they survive page refreshes.
 *  - Poll open ticket statuses every 30 s (only while the panel is open).
 *  - Fetch comment threads and track unread counts.
 *  - Post user replies back to JSM.
 */

import { computed, ref } from 'vue'
import { createSharedComposable, useIntervalFn } from '@vueuse/core'
import { useApi } from '@/config/apiFetcher'
import log from '@/utils/useLogger'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TicketRecord {
  key: string
  summary: string
  submittedAt: string
  sentryEventId?: string
  /** ISO timestamp — used to detect unread replies from support */
  lastReadAt?: string
  /** ISO timestamp when we first detected this ticket reached 'resolved' status.
   *  Tickets without this field are treated as active and included in status polls.
   *  Tickets with this field are excluded from polls and pruned after ARCHIVE_DAYS. */
  resolvedAt?: string
}

export interface TicketStatus {
  key: string
  summary: string
  status: 'open' | 'in_progress' | 'resolved'
  createdAt: string
}

export interface Comment {
  id: string
  author: string
  body: string
  created: string
  /** true = support agent reply (left bubble), false = user reply (right bubble) */
  isStaff: boolean
}

export interface Attachment {
  uuid: string
  filename: string
  mimeType: string
  size: number
  uploadedAt: string
  downloadUrl: string
  /** CloudFront signed URL resolved on thread open — ready for <img>/<audio>/<video> src */
  resolvedUrl?: string
}

export interface ActiveThread {
  key: string
  summary: string
  status: TicketStatus['status']
  comments: Comment[]
  attachments: Attachment[]
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'idi-fly-jsm-tickets'
const POLL_INTERVAL_MS = 30_000
const ARCHIVE_DAYS = 30   // resolved tickets older than this are pruned from localStorage
const JSM_BASE = import.meta.env.VITE_JSM_API_ENDPOINT as string | undefined

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

/** Remove resolved tickets whose resolvedAt is older than ARCHIVE_DAYS. */
function pruneArchivedTickets(tickets: TicketRecord[]): TicketRecord[] {
  const cutoff = Date.now() - ARCHIVE_DAYS * 86_400_000
  return tickets.filter((t) => {
    if (!t.resolvedAt) return true                              // active — keep
    return new Date(t.resolvedAt).getTime() > cutoff           // recently resolved — keep
  })
}

function loadFromStorage(): TicketRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const tickets = raw ? (JSON.parse(raw) as TicketRecord[]) : []
    const pruned = pruneArchivedTickets(tickets)
    // Persist the pruned set immediately so stale entries don't re-appear
    if (pruned.length !== tickets.length) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned)) } catch { /* ignore */ }
    }
    return pruned
  } catch {
    return []
  }
}

function persistToStorage(tickets: TicketRecord[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tickets))
  } catch (e) {
    log.warn('[JSM] Failed to persist tickets to localStorage', e)
  }
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

/**
 * Shared singleton — every call to useJsmTickets() returns the same reactive
 * state and the same API instance. This means FeedbackButton and FeedbackModal
 * both read the same storedTickets / unreadCount / latestStaffCommentAt, so a
 * ticket saved in the modal immediately updates the button's badge without
 * needing a page refresh or a cross-component event bus.
 *
 * createSharedComposable (VueUse) wraps the inner function so the first caller
 * sets up the singleton and all subsequent callers receive a reference to it.
 * The instance is torn down automatically when the last consumer unmounts.
 */
export const useJsmTickets = createSharedComposable(function () {
  const api = useApi()

  const storedTickets = ref<TicketRecord[]>(loadFromStorage())
  const liveStatuses = ref<Record<string, TicketStatus>>({})
  const activeThread = ref<ActiveThread | null>(null)
  const isLoadingThread = ref(false)
  const isSendingReply = ref(false)
  const threadError = ref<string | null>(null)

  /**
   * Tracks the ISO timestamp of the latest staff comment per ticket.
   * Populated by checkAllUnread() so we can compute the badge without
   * the user needing to open each thread.
   * Typed with `| undefined` so we can clear entries without TypeScript error.
   */
  const latestStaffCommentAt = ref<Record<string, string | undefined>>({})

  // ---------------------------------------------------------------------------
  // Unread count — tickets where support has replied after the user last read
  // ---------------------------------------------------------------------------

  const unreadCount = computed(() => {
    return storedTickets.value.filter((t) => {
      if (!t.lastReadAt) return false
      const latest = latestStaffCommentAt.value[t.key]
      if (!latest) return false
      return latest > t.lastReadAt
    }).length
  })

  // We track a separate "thread unread" flag that gets set after polling detects
  // a new comment while the thread is open.
  const threadHasNewComments = ref(false)

  // ---------------------------------------------------------------------------
  // Active / resolved split — derived from resolvedAt stamp on each record.
  // Tickets without resolvedAt are treated as active (polled every 30 s).
  // Tickets with resolvedAt are excluded from polls and loaded lazily on demand.
  // ---------------------------------------------------------------------------

  const activeTickets = computed(() => storedTickets.value.filter((t) => !t.resolvedAt))
  const resolvedTickets = computed(() => storedTickets.value.filter((t) => !!t.resolvedAt))

  /** True while lazy-loading statuses for the collapsed resolved section. */
  const isLoadingResolvedStatuses = ref(false)

  // ---------------------------------------------------------------------------
  // Save a newly created ticket
  // ---------------------------------------------------------------------------

  function saveTicket(record: TicketRecord): void {
    // Avoid duplicates
    if (storedTickets.value.some((t) => t.key === record.key)) return
    storedTickets.value.unshift(record)
    persistToStorage(storedTickets.value)
  }

  // ---------------------------------------------------------------------------
  // Mark thread as read (update lastReadAt)
  // ---------------------------------------------------------------------------

  function markRead(key: string): void {
    const ticket = storedTickets.value.find((t) => t.key === key)
    if (ticket) {
      ticket.lastReadAt = new Date().toISOString()
      persistToStorage(storedTickets.value)
    }
    // Treat the thread as read — clear the cached latest staff comment time
    // so the badge goes away immediately after opening.
    delete latestStaffCommentAt.value[key]
    threadHasNewComments.value = false
  }

  // ---------------------------------------------------------------------------
  // Background unread check — fetches comment lists for all stored tickets to
  // determine if any staff reply arrived after the user last read the thread.
  // Called once on mount (to populate the badge quickly) and can be called
  // at any time without side-effects.
  // ---------------------------------------------------------------------------

  async function checkAllUnread(): Promise<void> {
    // Resolved tickets don't receive new support replies — skip them to
    // avoid unnecessary comment-list fetches on mount.
    if (!JSM_BASE || activeTickets.value.length === 0) return

    await Promise.allSettled(
      activeTickets.value.map(async (t) => {
        if (!t.lastReadAt) return // never opened — nothing to mark unread
        try {
          const res = await api.get<{ comments: Comment[] }>(`${JSM_BASE}/${t.key}/comments`)
          const comments = res.data?.comments ?? []
          const staffComments = comments.filter((c) => c.isStaff)
          if (staffComments.length > 0) {
            const latest = staffComments.reduce((a, b) => (a.created > b.created ? a : b))
            latestStaffCommentAt.value = { ...latestStaffCommentAt.value, [t.key]: latest.created }
          }
        } catch (e) {
          log.warn('[JSM] checkAllUnread: failed for', t.key, e)
        }
      }),
    )
  }

  // ---------------------------------------------------------------------------
  // Resolve an attachment download URL
  // The stored downloadUrl is the authenticated API Gateway endpoint.
  // Calling it (with the Cognito JWT that useApi() attaches automatically)
  // returns { url: <CloudFront signed URL valid 1 h> } served from the CDN.
  // The caller should open that URL in a new tab.
  // ---------------------------------------------------------------------------

  async function resolveAttachmentUrl(downloadUrl: string): Promise<string | null> {
    try {
      const res = await api.get<{ url: string }>(downloadUrl)
      return res.data?.url ?? null
    } catch (e) {
      log.error('[JSM] Failed to resolve attachment download URL', e)
      return null
    }
  }

  // ---------------------------------------------------------------------------
  // Lazy-load statuses for the resolved section
  // Called only when the user expands the "Resolved" accordion — never on mount.
  // ---------------------------------------------------------------------------

  async function loadResolvedStatuses(): Promise<void> {
    if (!JSM_BASE || resolvedTickets.value.length === 0) return
    // Skip keys we already have a cached status for
    const toFetch = resolvedTickets.value.filter((t) => !liveStatuses.value[t.key])
    if (toFetch.length === 0) return

    isLoadingResolvedStatuses.value = true
    await Promise.allSettled(
      toFetch.map(async (t) => {
        try {
          const res = await api.get<TicketStatus>(`${JSM_BASE}/${t.key}`)
          if (res.data) liveStatuses.value = { ...liveStatuses.value, [t.key]: res.data }
        } catch (e) {
          log.warn('[JSM] Failed to load resolved status for', t.key, e)
        }
      }),
    )
    isLoadingResolvedStatuses.value = false
  }

  // ---------------------------------------------------------------------------
  // Refresh ticket statuses (called by poll + on open)
  // ---------------------------------------------------------------------------

  async function refreshStatuses(): Promise<void> {
    // Only poll tickets that are still active — resolved tickets don't change state
    // and excluding them cuts the number of API calls proportionally.
    if (!JSM_BASE || activeTickets.value.length === 0) return

    await Promise.allSettled(
      activeTickets.value.map(async (t) => {
        try {
          const res = await api.get<TicketStatus>(`${JSM_BASE}/${t.key}`)
          if (res.data) {
            liveStatuses.value = { ...liveStatuses.value, [t.key]: res.data }
            // Stamp resolvedAt the first time we see a resolved status so this
            // ticket is excluded from all future polls and auto-pruned after 30 days.
            if (res.data.status === 'resolved' && !t.resolvedAt) {
              t.resolvedAt = new Date().toISOString()
              persistToStorage(storedTickets.value)
            }
          }
        } catch (e) {
          log.warn('[JSM] Failed to refresh status for', t.key, e)
        }
      }),
    )
  }

  // ---------------------------------------------------------------------------
  // Open a ticket thread — fetch comments + mark as read
  // ---------------------------------------------------------------------------

  async function openThread(key: string): Promise<void> {
    if (!JSM_BASE) {
      threadError.value = 'JSM endpoint not configured'
      return
    }

    isLoadingThread.value = true
    threadError.value = null

    try {
      const [statusRes, commentsRes, attachmentsRes] = await Promise.all([
        api.get<TicketStatus>(`${JSM_BASE}/${key}`),
        api.get<{ comments: Comment[] }>(`${JSM_BASE}/${key}/comments`),
        api.get<{ attachments: Attachment[] }>(`${JSM_BASE}/${key}/attachments`).catch(() => ({ data: { attachments: [] } })),
      ])

      const status = statusRes.data
      const comments = commentsRes.data?.comments ?? []

      if (status) {
        liveStatuses.value = { ...liveStatuses.value, [key]: status }
      }

      const ticket = storedTickets.value.find((t) => t.key === key)
      const rawAttachments: Attachment[] = attachmentsRes.data?.attachments ?? []

      // Resolve CloudFront signed URLs for all attachments in parallel so the
      // thread can render inline previews (image thumbnails, audio/video players)
      // without a per-click API round-trip.  Failures are non-fatal — the
      // attachment still shows a download button in that case.
      const resolvedAttachments = await Promise.all(
        rawAttachments.map(async (att) => {
          try {
            const signedUrl = await resolveAttachmentUrl(att.downloadUrl)
            return { ...att, resolvedUrl: signedUrl ?? undefined }
          } catch {
            return att
          }
        }),
      )

      activeThread.value = {
        key,
        summary: status?.summary ?? ticket?.summary ?? key,
        status: status?.status ?? 'open',
        comments,
        attachments: resolvedAttachments,
      }

      markRead(key)
    } catch (e) {
      log.error('[JSM] Failed to open thread', key, e)
      threadError.value = 'Failed to load thread. Please try again.'
    } finally {
      isLoadingThread.value = false
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch attachments for a ticket
  // ---------------------------------------------------------------------------

  async function fetchAttachments(key: string): Promise<Attachment[]> {
    if (!JSM_BASE) return []
    try {
      const res = await api.get<{ attachments: Attachment[] }>(`${JSM_BASE}/${key}/attachments`)
      return res.data?.attachments ?? []
    } catch (e) {
      log.warn('[JSM] Failed to fetch attachments', key, e)
      return []
    }
  }

  // ---------------------------------------------------------------------------
  // Upload a non-image attachment to S3 via pre-signed PUT URL
  // ---------------------------------------------------------------------------

  async function uploadAttachment(
    key: string,
    file: { filename: string; mimeType: string; size: number; blob: Blob },
  ): Promise<{ uuid: string; downloadUrl: string; viewUrl: string } | null> {
    if (!JSM_BASE) return null
    try {
      // Step 1: request pre-signed PUT URL
      const res = await api.post<{
        uuid: string
        uploadUrl: string
        downloadUrl: string
        viewUrl: string
      }>(`${JSM_BASE}/${key}/attachments/upload`, {
        filename: file.filename,
        mimeType: file.mimeType,
        size: file.size,
      })

      const { uuid, uploadUrl, downloadUrl, viewUrl } = res.data!

      // Step 2: PUT directly to S3 (no auth header — pre-signed URL includes credentials).
      // Strip codec params to match the Content-Type used when signing
      // (e.g. "video/webm;codecs=vp9" → "video/webm").
      const baseMime = file.mimeType.split(';')[0].trim()
      const s3Res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': baseMime },
        body: file.blob,
      })
      if (!s3Res.ok) {
        throw new Error(`S3 upload failed: ${s3Res.status} ${s3Res.statusText}`)
      }

      return { uuid, downloadUrl, viewUrl }
    } catch (e) {
      log.error('[JSM] Failed to upload attachment', file.filename, e)
      return null
    }
  }

  // ---------------------------------------------------------------------------
  // Refresh comments for the currently open thread (used by poll)
  // ---------------------------------------------------------------------------

  async function refreshActiveThread(): Promise<void> {
    const key = activeThread.value?.key
    if (!key || !JSM_BASE) return

    try {
      const res = await api.get<{ comments: Comment[] }>(`${JSM_BASE}/${key}/comments`)
      const freshComments = res.data?.comments ?? []

      const prevCount = activeThread.value!.comments.length
      activeThread.value = { ...activeThread.value!, comments: freshComments }

      if (freshComments.length > prevCount) {
        threadHasNewComments.value = true
        markRead(key)
      }
    } catch (e) {
      log.warn('[JSM] Failed to refresh thread comments', e)
    }
  }

  // ---------------------------------------------------------------------------
  // Post a reply
  // ---------------------------------------------------------------------------

  async function postReply(key: string, body: string): Promise<boolean> {
    if (!JSM_BASE) return false

    isSendingReply.value = true

    // Optimistically append immediately for instant UI feedback
    const optimisticId = `local-${Date.now()}`
    if (activeThread.value?.key === key) {
      activeThread.value.comments.push({
        id: optimisticId,
        author: 'You',
        body,
        created: new Date().toISOString(),
        isStaff: false,
      })
    }

    try {
      await api.post(`${JSM_BASE}/${key}/reply`, { body })
      return true
    } catch (e) {
      log.error('[JSM] Failed to post reply', e)
      // Roll back optimistic comment on failure
      if (activeThread.value?.key === key) {
        activeThread.value.comments = activeThread.value.comments.filter(
          (c) => c.id !== optimisticId,
        )
      }
      return false
    } finally {
      isSendingReply.value = false
    }
  }

  // ---------------------------------------------------------------------------
  // Close thread
  // ---------------------------------------------------------------------------

  function closeThread(): void {
    activeThread.value = null
    threadError.value = null
    threadHasNewComments.value = false
  }

  // ---------------------------------------------------------------------------
  // Polling — call start/stop from the parent component
  // ---------------------------------------------------------------------------

  const { pause: stopPolling, resume: startPolling } = useIntervalFn(
    async () => {
      await refreshStatuses()
      if (activeThread.value) {
        await refreshActiveThread()
      }
    },
    POLL_INTERVAL_MS,
    { immediate: false },
  )

  return {
    // State
    storedTickets,
    activeTickets,
    resolvedTickets,
    liveStatuses,
    activeThread,
    isLoadingThread,
    isSendingReply,
    isLoadingResolvedStatuses,
    threadError,
    unreadCount,
    threadHasNewComments,
    latestStaffCommentAt,

    // Actions
    saveTicket,
    markRead,
    refreshStatuses,
    checkAllUnread,
    loadResolvedStatuses,
    openThread,
    closeThread,
    postReply,
    fetchAttachments,
    uploadAttachment,
    resolveAttachmentUrl,
    startPolling,
    stopPolling,
  }
})
